import type {Options, SeriesOptionsType} from 'highcharts';
import CubeIndex, {AxisItem} from '../cube/CubeIndex';
import {percentAxisCap} from '../render/axisBounds';
import {itemColor} from '../render/colors';
import {axisLabelFormat, categoryLabel, escapeHtml, formatValue, tooltipHtml} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {Cube, CubeMeasure, MEASURES_CHANNEL, RenderContext, RenderSpec} from '../types';

export type XyKind = 'bar' | 'line' | 'area';

export interface XyChannels {
    x: string;
    series?: string;
}

/**
 * Resolves what sits on the x axis and what makes the series. Encoding is normally complete; the fallback only
 * covers a measure-only cube (everything is then spread over `$measures`).
 */
export function resolveChannels(encoding: RenderSpec['encoding'], measureCount: number): XyChannels {
    const x = typeof encoding.x === 'string' ? encoding.x : MEASURES_CHANNEL;
    const series = typeof encoding.series === 'string' ? encoding.series : undefined;

    if (!series && x !== MEASURES_CHANNEL && measureCount > 1) {
        return {x, series: MEASURES_CHANNEL};
    }
    return {x, series};
}

export function positions(channels: XyChannels, x: AxisItem, series?: AxisItem): Record<string, number> {
    const result: Record<string, number> = {};
    if (channels.x !== MEASURES_CHANNEL) {
        result[channels.x] = x.index;
    }
    if (channels.series && channels.series !== MEASURES_CHANNEL && series) {
        result[channels.series] = series.index;
    }
    return result;
}

/** With x=$measures and no series every x item is its own measure, so measures[0] must not tint the series. */
export function seriesColorMeasure(channels: XyChannels, seriesMeasure: CubeMeasure): CubeMeasure | undefined {
    return channels.x === MEASURES_CHANNEL && !channels.series ? undefined : seriesMeasure;
}

/**
 * Colour of one x item when several of them share a single series. Only items that carry a colour of their own
 * (member colour, role, or a measure colour on $measures) stand out; the rest keep the series colour.
 */
export function xPointColor(xItem: AxisItem, xIndex: number, fallback: string, ctx: RenderContext): string {
    return xItem.member?.color || xItem.member?.role || xItem.measure?.color
        ? itemColor(xItem.member, xItem.measure, xIndex, ctx)
        : fallback;
}

export default function renderXy(kind: XyKind, cube: Cube, spec: RenderSpec, ctx: RenderContext): Options {
    const index = new CubeIndex(cube);
    const channels = resolveChannels(spec.encoding, cube.measures.length);
    const xItems = index.items(channels.x);
    const seriesItems = channels.series ? index.items(channels.series) : [undefined];
    const options = spec.options;
    const stacking: string | undefined = options.stacking && options.stacking !== 'none' ? options.stacking : undefined;
    const smooth = options.smooth === true;

    const measureFor = (x: AxisItem, series?: AxisItem) => x.measure ?? series?.measure ?? cube.measures[0];

    // Several x items on one series colour themselves only when nothing else tells series apart.
    const colorByX = seriesItems.length === 1;

    const plottedMeasures = new Map<string, CubeMeasure>();
    const plottedValues: Array<number | null | undefined> = [];

    const series: SeriesOptionsType[] = seriesItems.map((seriesItem, seriesIndex) => {
        const seriesMeasure = seriesItem?.measure ?? cube.measures[0];
        const color = seriesItem
            ? itemColor(seriesItem.member, seriesItem.measure, seriesIndex, ctx)
            : itemColor(undefined, seriesColorMeasure(channels, seriesMeasure), 0, ctx);

        const data = xItems.map((xItem, xIndex) => {
            const measure = measureFor(xItem, seriesItem);
            const cell = index.value(positions(channels, xItem, seriesItem), measure.id);
            plottedMeasures.set(measure.id, measure);
            plottedValues.push(cell?.value);
            const lowBase = isLowBase(cube, cell);
            const pointColor = colorByX ? xPointColor(xItem, xIndex, color, ctx) : color;

            return {
                y: cell?.value ?? null,
                ...(colorByX && pointColor !== color ? {color: pointColor} : {}),
                ...(lowBase ? lowBaseAttributes(kind === 'bar' ? 'bar' : 'line', pointColor, ctx) : {}),
                custom: {
                    count: cell?.count ?? 0,
                    lowBase,
                    valueText: formatValue(cell?.value, measure, ctx),
                    tooltip: tooltipHtml({
                        heading: [seriesItem && seriesItem.label !== xItem.label ? seriesItem.label : '', xItem.label],
                        color: pointColor,
                        measure,
                        cell,
                        lowBase,
                    }, ctx),
                },
            };
        });

        return {
            id: seriesItem?.id ?? seriesMeasure.id,
            name: escapeHtml(seriesItem?.label ?? seriesMeasure.label),
            color,
            data,
        } as SeriesOptionsType;
    });

    const horizontal = kind === 'bar' && options.orientation === 'horizontal';
    const chartType = kind === 'bar'
        ? (horizontal ? 'bar' : 'column')
        : kind === 'line' ? (smooth ? 'spline' : 'line') : (smooth ? 'areaspline' : 'area');

    const result: Options = {
        chart: {type: chartType},
        title: {text: undefined},
        credits: {enabled: false},
        xAxis: {categories: xItems.map(categoryLabel), crosshair: kind !== 'bar'},
        yAxis: {
            title: {text: undefined},
            labels: {format: stacking === 'percent' ? '{value}%' : axisLabelFormat(cube.measures[0])},
            ...(stacking ? {} : percentAxisCap(Array.from(plottedMeasures.values()), plottedValues)),
        },
        legend: {enabled: seriesItems.length > 1},
        tooltip: {shared: false, headerFormat: '', pointFormat: '{point.custom.tooltip}'},
        plotOptions: {
            series: {
                stacking: stacking as any,
                dataLabels: {enabled: options.dataLabels === true, format: '{point.custom.valueText}'},
                ...(kind === 'line' ? {
                    // null outline falls back to the point colour (Highcharts default is white), as in v1
                    marker: {enabled: options.markers !== false, radius: 5, lineWidth: 2, lineColor: null},
                } : {}),
            },
            // the base minPointLength would draw zeros as slivers and skew a stack (a 100 % stack most of all)
            ...(stacking ? {column: {minPointLength: 0}, bar: {minPointLength: 0}} : {}),
        },
        series,
    };

    return result;
}
